import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { loadError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { euro, CLEANING } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const ROOM_PLACEHOLDER = "https://images.unsplash.com/photo-1631049307264-da0ec9d70304?auto=format&fit=crop&w=800&q=70";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Users, Pencil, Trash2, Sparkles, Building2, Layers, ChevronDown, ChevronsUpDown, ChevronsDownUp } from "lucide-react";
import { toast } from "sonner";

export const BUILDINGS = { nord: "Edificio Nord", sud: "Edificio Sud" };
export const FLOORS = ["Piano terra", "Primo piano", "Secondo piano", "Terzo piano", "Quarto piano"];

const empty = { name: "", type: "", base_rate: 80, max_guests: 2, amenities: [], photo_url: "", description: "", cleaning_status: "clean", building: "nord", floor: 0 };

export default function Rooms() {
  const { building } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rooms, setRooms] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [amenityText, setAmenityText] = useState("");
  const [generating, setGenerating] = useState(false);
  const mine = rooms.filter((r) => (r.building || "nord") === building);
  const doubles = mine.filter((r) => r.is_double).length;
  const [collapsed, setCollapsed] = useState(() => { try { return JSON.parse(localStorage.getItem("rooms_collapsed") || "{}"); } catch { return {}; } });
  useEffect(() => { localStorage.setItem("rooms_collapsed", JSON.stringify(collapsed)); }, [collapsed]);
  const isCollapsed = (fl) => !!collapsed[`${building}-${fl}`];
  const toggleFloor = (fl) => setCollapsed((c) => ({ ...c, [`${building}-${fl}`]: !c[`${building}-${fl}`] }));
  const allCollapsed = FLOORS.every((_, fl) => isCollapsed(fl));
  const toggleAll = () => setCollapsed((c) => ({ ...c, ...Object.fromEntries(FLOORS.map((_, fl) => [`${building}-${fl}`, !allCollapsed])) }));

  const generatePhoto = async () => {
    if (!form.name) { toast.error("Inserisci prima il nome della camera"); return; }
    setGenerating(true);
    try {
      const { data } = await api.post("/rooms/ai-photo", {
        name: form.name, type: form.type, description: form.description,
        amenities: amenityText.split(",").map((s) => s.trim()).filter(Boolean),
      });
      set("photo_url", data.photo_url);
      toast.success("Foto generata con l'AI");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setGenerating(false); }
  };

  const load = () => api.get("/rooms").then((r) => setRooms(r.data)).catch(loadError);
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ ...empty, building }); setAmenityText(""); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...empty, ...r, building: r.building || "nord", floor: r.floor ?? 0 }); setAmenityText((r.amenities || []).join(", ")); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name || !form.type) { toast.error("Nome e tipologia obbligatori"); return; }
    const { is_double, ...rest } = form;
    const payload = { ...rest, base_rate: Number(form.base_rate), max_guests: Number(form.max_guests), floor: Number(form.floor), amenities: amenityText.split(",").map((s) => s.trim()).filter(Boolean) };
    try {
      if (editing) await api.put(`/rooms/${editing.id}`, payload);
      else await api.post("/rooms", payload);
      toast.success(editing ? "Camera aggiornata" : "Camera creata");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const cycleCleaning = async (r) => {
    const order = ["clean", "dirty", "cleaning"];
    const next = order[(order.indexOf(r.cleaning_status) + 1) % 3];
    await api.patch(`/rooms/${r.id}/cleaning`, { cleaning_status: next });
    load();
  };

  const remove = async (r) => {
    if (!window.confirm(`Eliminare ${r.name}?`)) return;
    try { await api.delete(`/rooms/${r.id}`); toast.success("Camera eliminata"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!BUILDINGS[building]) return <Navigate to="/camere/nord" replace />;

  const RoomCard = ({ r }) => {
    const c = CLEANING[r.cleaning_status] || CLEANING.clean;
    return (
      <Card className="overflow-hidden border-border shadow-sm group animate-fade-up" data-testid={`room-card-${r.id}`}>
        <div className="h-28 overflow-hidden relative">
          <img src={r.photo_url || ROOM_PLACEHOLDER} alt={r.name} onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = ROOM_PLACEHOLDER; }} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
          <button data-testid={`cleaning-toggle-${r.id}`} onClick={() => cycleCleaning(r)} className="absolute top-2 right-2">
            <Badge className={`${c.bg} ${c.text} border-0 shadow-sm cursor-pointer text-[10px] px-2 py-0`}>{c.label}</Badge>
          </button>
          {r.is_double && <Badge className="absolute top-2 left-2 bg-accent text-accent-foreground border-0 shadow-sm text-[10px] px-2 py-0">Doppia</Badge>}
        </div>
        <div className="p-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="font-heading text-sm font-semibold truncate">{r.name}</h3>
              <p className="text-[11px] text-muted-foreground truncate">{r.type}</p>
            </div>
            <div className="text-right shrink-0">
              <p className="font-heading text-sm font-semibold text-accent">{euro(r.base_rate)}</p>
              <p className="text-[10px] text-muted-foreground">/notte</p>
            </div>
          </div>
          <div className="flex items-center gap-1 text-[11px] text-muted-foreground mt-2">
            <Users className="w-3 h-3" /> Fino a {r.max_guests} ospiti
          </div>
          <div className="flex flex-wrap gap-1 mt-2">
            {(r.amenities || []).slice(0, 3).map((a) => (
              <span key={a} className="text-[10px] bg-secondary text-secondary-foreground rounded-full px-2 py-0.5">{a}</span>
            ))}
            {(r.amenities || []).length > 3 && <span className="text-[10px] text-muted-foreground px-1">+{r.amenities.length - 3}</span>}
          </div>
          <div className="flex gap-1.5 mt-3 pt-3 border-t border-border">
            <Button variant="outline" size="sm" data-testid={`edit-room-${r.id}`} onClick={() => openEdit(r)} className="flex-1 h-8 text-xs">
              <Pencil className="w-3 h-3 mr-1" /> Modifica
            </Button>
            {isAdmin && (
              <Button variant="outline" size="sm" data-testid={`delete-room-${r.id}`} onClick={() => remove(r)} className="h-8 w-8 p-0 text-destructive hover:text-destructive">
                <Trash2 className="w-3 h-3" />
              </Button>
            )}
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-accent font-semibold inline-flex items-center gap-2"><Building2 className="w-3.5 h-3.5" />Gestione camere</p>
          <h1 className="font-display text-4xl sm:text-5xl mt-1" data-testid="rooms-title">Camere {BUILDINGS[building]}</h1>
          <p className="text-sm text-muted-foreground mt-1" data-testid="rooms-summary">{mine.length} camere · {doubles} doppie · {mine.length - doubles} altre tipologie</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" data-testid="toggle-all-floors" onClick={toggleAll} className="rounded-full h-11 px-4">
            {allCollapsed ? <ChevronsUpDown className="w-4 h-4 mr-2" /> : <ChevronsDownUp className="w-4 h-4 mr-2" />}{allCollapsed ? "Mostra tutti i piani" : "Nascondi tutti i piani"}
          </Button>
          <Button data-testid="new-room-button" onClick={openNew} className="bg-accent hover:bg-accent/90 text-accent-foreground rounded-full h-11 px-5 font-heading">
            <Plus className="w-4 h-4 mr-2" /> Nuova Camera
          </Button>
        </div>
      </div>

      {FLOORS.map((label, fl) => {
        const list = mine.filter((r) => (r.floor ?? 0) === fl);
        const d = list.filter((r) => r.is_double).length;
        const hidden = isCollapsed(fl);
        return (
          <section key={fl} data-testid={`floor-${building}-${fl}`} data-collapsed={hidden} className="space-y-3">
            <button type="button" data-testid={`toggle-floor-${building}-${fl}`} onClick={() => toggleFloor(fl)} aria-expanded={!hidden} className="w-full flex items-center gap-3 rounded-lg px-1 py-1 -mx-1 text-left hover:bg-secondary/60 transition-colors">
              <span className="w-8 h-8 rounded-lg bg-accent/15 text-accent grid place-items-center"><Layers className="w-4 h-4" /></span>
              <h2 className="font-heading text-lg">{label}</h2>
              <span className="text-xs text-muted-foreground rounded-full border border-border px-2.5 py-0.5" data-testid={`floor-count-${building}-${fl}`}>{list.length} camere · {d} doppie</span>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground ml-auto">{BUILDINGS[building]}</span>
              <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${hidden ? "-rotate-90" : ""}`} />
            </button>
            {!hidden && (list.length === 0
              ? <p className="text-xs text-muted-foreground pl-11">Nessuna camera su questo piano.</p>
              : <div className="grid sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid={`rooms-grid-${fl}`}>{list.map((r) => <RoomCard key={r.id} r={r} />)}</div>)}
          </section>
        );
      })}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-accent" /> {editing ? "Modifica camera" : "Nuova camera"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider">Nome</Label><Input data-testid="room-name-input" className="mt-1.5" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Camera Smeraldo" /></div>
              <div><Label className="text-xs uppercase tracking-wider">Tipologia</Label><Input data-testid="room-type-input" className="mt-1.5" value={form.type} onChange={(e) => set("type", e.target.value)} placeholder="Doppia, Matrimoniale, Singola…" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">Edificio</Label>
                <Select value={form.building} onValueChange={(v) => set("building", v)}>
                  <SelectTrigger data-testid="room-building-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(BUILDINGS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Piano</Label>
                <Select value={String(form.floor)} onValueChange={(v) => set("floor", Number(v))}>
                  <SelectTrigger data-testid="room-floor-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{FLOORS.map((l, i) => <SelectItem key={i} value={String(i)}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs uppercase tracking-wider">Prezzo €</Label><Input type="number" className="mt-1.5" value={form.base_rate} onChange={(e) => set("base_rate", e.target.value)} /></div>
              <div><Label className="text-xs uppercase tracking-wider">Max ospiti</Label><Input type="number" className="mt-1.5" value={form.max_guests} onChange={(e) => set("max_guests", e.target.value)} /></div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Pulizia</Label>
                <Select value={form.cleaning_status} onValueChange={(v) => set("cleaning_status", v)}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="clean">Pulita</SelectItem>
                    <SelectItem value="dirty">Da pulire</SelectItem>
                    <SelectItem value="cleaning">In pulizia</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Foto</Label>
              <div className="flex gap-2 mt-1.5">
                <Input data-testid="room-photo-url" value={form.photo_url || ""} onChange={(e) => set("photo_url", e.target.value)} placeholder="URL immagine" />
                <Button type="button" variant="outline" data-testid="ai-photo-button" onClick={generatePhoto} disabled={generating} className="shrink-0 border-accent text-accent hover:bg-accent/10">
                  <Sparkles className={`w-4 h-4 mr-1.5 ${generating ? "animate-spin" : ""}`} />{generating ? "Generazione…" : "Genera con AI"}
                </Button>
              </div>
              {form.photo_url && <img src={form.photo_url} alt="anteprima" data-testid="room-photo-preview" className="mt-2 h-32 w-full object-cover rounded-lg border border-border" />}
            </div>
            <div><Label className="text-xs uppercase tracking-wider">Servizi (separati da virgola)</Label><Input className="mt-1.5" value={amenityText} onChange={(e) => setAmenityText(e.target.value)} placeholder="Wi-Fi, Aria condizionata" /></div>
            <div><Label className="text-xs uppercase tracking-wider">Descrizione</Label><Textarea className="mt-1.5" rows={2} value={form.description || ""} onChange={(e) => set("description", e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button data-testid="save-room-button" onClick={save} className="bg-accent hover:bg-accent/90 text-accent-foreground">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
